/**
 * BUYER-INFRA-R1 — Customer buyer / delivery profile API client.
 * Same-origin Pages proxies → Worker /api/miniapp/buyer-profiles* + delivery-profiles.
 * Retail CONSUMER is the implicit default; never required for checkout.
 */
import { waitForTelegramInitData } from "../lib/telegram.js";
import { fetchWithTimeout, isFetchTimeout } from "./fetch-timeout-v2.js";
import { storeV2AuthHeaders } from "./store-v2-auth-headers-v2.js";

const BASE = "/api/store-v2/buyer-profiles";
const POLICY_BASE = "/api/store-v2/buyer-verification-policy";
const DELIVERY_BASE = "/api/store-v2/delivery-profiles";
const TIMEOUT_MS = 12_000;

export type BuyerProfileType = "FOP" | "LEGAL_ENTITY";

export type BuyerVerificationStatus =
  | "DRAFT"
  | "SUBMITTED"
  | "AUTO_CHECK"
  | "ACCOUNTANT_REVIEW"
  | "APPROVED"
  | "NEEDS_CHANGES"
  | "REJECTED"
  | "SUSPENDED"
  | "REVERIFY_REQUIRED"
  | "NOT_REQUIRED"
  | string;

export type BuyerProfilePublic = {
  id: string;
  type: BuyerProfileType | string;
  display_name: string;
  legal_name: string | null;
  tax_id_masked: string | null;
  tax_id_type: string | null;
  legal_address: string | null;
  email: string | null;
  phone: string | null;
  contact_name: string | null;
  verification_status: BuyerVerificationStatus;
  verification_status_uk: string;
  verification_version: number;
  selectable_as_verified_buyer: boolean;
  approved_at: string | null;
  review_note: string | null;
  application_number?: string | null;
  submitted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type BuyerDocumentCard = {
  id: string;
  document_type: string;
  original_filename: string | null;
  mime_type: string | null;
  byte_size: number | null;
  uploaded_at: string | null;
  scan_status: string | null;
  review_status: string | null;
  sha256_prefix: string | null;
};

export type DeliveryProfilePublic = {
  id: string;
  label: string;
  recipient_name: string;
  recipient_phone: string;
  delivery_method?: string | null;
  city_ref?: string | null;
  city_name?: string | null;
  warehouse_ref?: string | null;
  warehouse_name?: string | null;
  address_line?: string | null;
  is_default?: boolean;
};

export type BuyerProfilesListResult = {
  ok: boolean;
  retail_default: {
    type: "CONSUMER";
    label_uk: string;
    selectable_as_verified_buyer: boolean;
    tax_id_required: boolean;
  };
  profiles: BuyerProfilePublic[];
  error?: string;
  message?: string;
};

function mapProfile(raw: Record<string, unknown>): BuyerProfilePublic {
  return {
    id: String(raw.id ?? ""),
    type: String(raw.type ?? ""),
    display_name: String(raw.display_name ?? ""),
    legal_name: raw.legal_name == null ? null : String(raw.legal_name),
    tax_id_masked: raw.tax_id_masked == null ? null : String(raw.tax_id_masked),
    tax_id_type: raw.tax_id_type == null ? null : String(raw.tax_id_type),
    legal_address: raw.legal_address == null ? null : String(raw.legal_address),
    email: raw.email == null ? null : String(raw.email),
    phone: raw.phone == null ? null : String(raw.phone),
    contact_name: raw.contact_name == null ? null : String(raw.contact_name),
    verification_status: String(raw.verification_status ?? "DRAFT"),
    verification_status_uk: String(raw.verification_status_uk ?? "Чернетка"),
    verification_version: Number(raw.verification_version ?? 1),
    selectable_as_verified_buyer: Boolean(raw.selectable_as_verified_buyer),
    approved_at: raw.approved_at == null ? null : String(raw.approved_at),
    review_note: raw.review_note == null ? null : String(raw.review_note),
    application_number: raw.application_number == null ? null : String(raw.application_number),
    submitted_at: raw.submitted_at == null ? null : String(raw.submitted_at),
    created_at: raw.created_at == null ? null : String(raw.created_at),
    updated_at: raw.updated_at == null ? null : String(raw.updated_at),
  };
}

function mapDoc(raw: Record<string, unknown>): BuyerDocumentCard {
  return {
    id: String(raw.id ?? ""),
    document_type: String(raw.document_type ?? ""),
    original_filename: raw.original_filename == null ? null : String(raw.original_filename),
    mime_type: raw.mime_type == null ? null : String(raw.mime_type),
    byte_size: raw.byte_size == null ? null : Number(raw.byte_size),
    uploaded_at: raw.uploaded_at == null ? null : String(raw.uploaded_at),
    scan_status: raw.scan_status == null ? null : String(raw.scan_status),
    review_status: raw.review_status == null ? null : String(raw.review_status),
    sha256_prefix: raw.sha256_prefix == null ? null : String(raw.sha256_prefix),
  };
}

async function readJson(res: Response): Promise<Record<string, unknown>> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function networkFail(err: unknown): { ok: false; error: string; message: string } {
  const timeout = isFetchTimeout(err);
  return {
    ok: false,
    error: timeout ? "timeout" : "network",
    message: timeout
      ? "Сервер відповідає довше, ніж зазвичай. Спробуйте ще раз."
      : "Не вдалося звʼязатися з сервером. Спробуйте ще раз.",
  };
}

export function buyerStatusRail(status: string): string {
  const s = status.toUpperCase();
  if (s === "APPROVED") return "completed";
  if (s === "REJECTED" || s === "SUSPENDED") return "cancelled";
  if (s === "NEEDS_CHANGES" || s === "REVERIFY_REQUIRED") return "awaiting";
  if (s === "SUBMITTED" || s === "AUTO_CHECK" || s === "ACCOUNTANT_REVIEW") return "shipping";
  return "awaiting";
}

export function buyerTypeLabelUk(type: string): string {
  const t = type.toUpperCase();
  if (t === "FOP") return "ФОП";
  if (t === "LEGAL_ENTITY") return "Компанія";
  return "Особисто";
}

export async function fetchBuyerProfilesV2(): Promise<BuyerProfilesListResult> {
  try {
    await waitForTelegramInitData(2500);
    const res = await fetchWithTimeout(
      BASE,
      { method: "GET", headers: storeV2AuthHeaders() },
      TIMEOUT_MS,
    );
    const json = await readJson(res);
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        retail_default: {
          type: "CONSUMER",
          label_uk: "Особисто · Роздрібний покупець",
          selectable_as_verified_buyer: true,
          tax_id_required: false,
        },
        profiles: [],
        error: String(json.error ?? `http_${res.status}`),
        message: String(json.message_uk ?? json.message ?? "Не вдалося завантажити профілі покупця."),
      };
    }
    const rows = Array.isArray(json.profiles) ? json.profiles : [];
    const retail = (json.retail_default ?? {}) as Record<string, unknown>;
    return {
      ok: true,
      retail_default: {
        type: "CONSUMER",
        label_uk: String(retail.label_uk ?? "Особисто · Роздрібний покупець"),
        selectable_as_verified_buyer: retail.selectable_as_verified_buyer !== false,
        tax_id_required: Boolean(retail.tax_id_required),
      },
      profiles: rows.map((r) => mapProfile(r as Record<string, unknown>)),
    };
  } catch (err) {
    const fail = networkFail(err);
    return {
      ok: false,
      retail_default: {
        type: "CONSUMER",
        label_uk: "Особисто · Роздрібний покупець",
        selectable_as_verified_buyer: true,
        tax_id_required: false,
      },
      profiles: [],
      error: fail.error,
      message: fail.message,
    };
  }
}

export async function createBuyerProfileV2(input: {
  type: BuyerProfileType;
  legal_name: string;
  display_name?: string;
  tax_id: string;
  legal_address?: string | null;
  email?: string | null;
  phone?: string | null;
  contact_name?: string | null;
}): Promise<{ ok: boolean; profile?: BuyerProfilePublic; reused?: boolean; error?: string; message?: string }> {
  try {
    await waitForTelegramInitData(2500);
    const res = await fetchWithTimeout(
      BASE,
      {
        method: "POST",
        headers: storeV2AuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          type: input.type,
          legal_name: input.legal_name,
          display_name: input.display_name ?? input.legal_name,
          tax_id: input.tax_id,
          legal_address: input.legal_address ?? null,
          email: input.email ?? null,
          phone: input.phone ?? null,
          contact_name: input.contact_name ?? null,
        }),
      },
      TIMEOUT_MS,
    );
    const json = await readJson(res);
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        error: String(json.error ?? `http_${res.status}`),
        message: String(json.message_uk ?? json.message ?? "Не вдалося створити профіль."),
      };
    }
    return {
      ok: true,
      reused: Boolean(json.reused),
      ...(json.profile
        ? { profile: mapProfile(json.profile as Record<string, unknown>) }
        : {}),
    };
  } catch (err) {
    return networkFail(err);
  }
}

export async function submitBuyerProfileV2(
  profileId: string,
  input?: { verification_consent?: boolean; verification_consent_version?: string },
): Promise<{
  ok: boolean;
  profile?: BuyerProfilePublic;
  application_number?: string;
  error?: string;
  message?: string;
}> {
  try {
    await waitForTelegramInitData(2500);
    const res = await fetchWithTimeout(
      `${BASE}/${encodeURIComponent(profileId)}/submit`,
      {
        method: "POST",
        headers: storeV2AuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          verification_consent: input?.verification_consent === true,
          verification_consent_version: input?.verification_consent_version,
        }),
      },
      TIMEOUT_MS,
    );
    const json = await readJson(res);
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        error: String(json.error ?? `http_${res.status}`),
        message: String(json.message_uk ?? json.message ?? "Не вдалося надіслати на перевірку."),
      };
    }
    const out: {
      ok: true;
      profile?: BuyerProfilePublic;
      application_number?: string;
    } = { ok: true };
    if (json.application_number != null) {
      out.application_number = String(json.application_number);
    }
    if (json.profile) out.profile = mapProfile(json.profile as Record<string, unknown>);
    return out;
  } catch (err) {
    return networkFail(err);
  }
}

export type BuyerChecklistItem = {
  document_type: string;
  label_uk: string;
  explanation_uk: string;
  requirement: string;
  satisfied: boolean;
  uploaded_count: number;
};

export async function fetchBuyerVerificationPolicyV2(): Promise<{
  ok: boolean;
  policy_version?: string;
  consent_version?: string;
  consent_text_uk?: string;
}> {
  try {
    await waitForTelegramInitData(2500);
    const res = await fetchWithTimeout(POLICY_BASE, { method: "GET", headers: storeV2AuthHeaders() }, TIMEOUT_MS);
    const json = await readJson(res);
    if (!res.ok || json.ok === false) return { ok: false };
    return {
      ok: true,
      policy_version: String(json.policy_version ?? ""),
      consent_version: String(json.consent_version ?? ""),
      consent_text_uk: String(json.consent_text_uk ?? ""),
    };
  } catch {
    return { ok: false };
  }
}

export async function fetchBuyerProfileChecklistV2(profileId: string): Promise<{
  ok: boolean;
  documents: BuyerChecklistItem[];
  error?: string;
}> {
  try {
    await waitForTelegramInitData(2500);
    const res = await fetchWithTimeout(
      `${BASE}/${encodeURIComponent(profileId)}/checklist`,
      { method: "GET", headers: storeV2AuthHeaders() },
      TIMEOUT_MS,
    );
    const json = await readJson(res);
    if (!res.ok || json.ok === false) {
      return { ok: false, documents: [], error: String(json.error ?? "checklist_failed") };
    }
    const rows = Array.isArray(json.documents) ? json.documents : [];
    return {
      ok: true,
      documents: rows.map((r) => {
        const row = r as Record<string, unknown>;
        return {
          document_type: String(row.document_type ?? ""),
          label_uk: String(row.label_uk ?? row.document_type ?? ""),
          explanation_uk: String(row.explanation_uk ?? ""),
          requirement: String(row.requirement ?? "OPTIONAL"),
          satisfied: Boolean(row.satisfied),
          uploaded_count: Number(row.uploaded_count ?? 0),
        };
      }),
    };
  } catch {
    return { ok: false, documents: [] };
  }
}

export async function patchBuyerProfileV2(
  profileId: string,
  body: Record<string, unknown>,
): Promise<{ ok: boolean; profile?: BuyerProfilePublic; error?: string; message?: string }> {
  try {
    await waitForTelegramInitData(2500);
    const res = await fetchWithTimeout(
      `${BASE}/${encodeURIComponent(profileId)}`,
      {
        method: "PATCH",
        headers: storeV2AuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(body),
      },
      TIMEOUT_MS,
    );
    const json = await readJson(res);
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        error: String(json.error ?? `http_${res.status}`),
        message: String(json.message_uk ?? json.message ?? "Не вдалося оновити профіль."),
      };
    }
    return {
      ok: true,
      ...(json.profile ? { profile: mapProfile(json.profile as Record<string, unknown>) } : {}),
    };
  } catch (err) {
    return networkFail(err);
  }
}

export async function uploadBuyerDocumentV2(input: {
  profileId: string;
  file: File;
  documentType?: string;
}): Promise<{ ok: boolean; document?: BuyerDocumentCard; error?: string; message?: string }> {
  try {
    await waitForTelegramInitData(2500);
    const form = new FormData();
    form.append("file", input.file);
    form.append("document_type", input.documentType ?? "REGISTRATION_EVIDENCE");
    const res = await fetchWithTimeout(
      `${BASE}/${encodeURIComponent(input.profileId)}/documents`,
      {
        method: "POST",
        headers: storeV2AuthHeaders(),
        body: form,
      },
      30_000,
    );
    const json = await readJson(res);
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        error: String(json.error ?? `http_${res.status}`),
        message: String(json.message_uk ?? json.message ?? "Не вдалося завантажити документ."),
      };
    }
    return {
      ok: true,
      ...(json.document
        ? { document: mapDoc(json.document as Record<string, unknown>) }
        : {}),
    };
  } catch (err) {
    return networkFail(err);
  }
}

export async function listBuyerDocumentsV2(
  profileId: string,
): Promise<{ ok: boolean; documents: BuyerDocumentCard[]; error?: string; message?: string }> {
  try {
    await waitForTelegramInitData(2500);
    const res = await fetchWithTimeout(
      `${BASE}/${encodeURIComponent(profileId)}/documents`,
      { method: "GET", headers: storeV2AuthHeaders() },
      TIMEOUT_MS,
    );
    const json = await readJson(res);
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        documents: [],
        error: String(json.error ?? `http_${res.status}`),
        message: String(json.message_uk ?? json.message ?? "Не вдалося завантажити документи."),
      };
    }
    const rows = Array.isArray(json.documents) ? json.documents : [];
    return { ok: true, documents: rows.map((r) => mapDoc(r as Record<string, unknown>)) };
  } catch (err) {
    const fail = networkFail(err);
    return { ok: false, documents: [], error: fail.error, message: fail.message };
  }
}

export async function fetchDeliveryProfilesV2(): Promise<{
  ok: boolean;
  profiles: DeliveryProfilePublic[];
  error?: string;
  message?: string;
}> {
  try {
    await waitForTelegramInitData(2500);
    const res = await fetchWithTimeout(
      DELIVERY_BASE,
      { method: "GET", headers: storeV2AuthHeaders() },
      TIMEOUT_MS,
    );
    const json = await readJson(res);
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        profiles: [],
        error: String(json.error ?? `http_${res.status}`),
        message: String(json.message_uk ?? json.message ?? "Не вдалося завантажити адреси."),
      };
    }
    const rows = Array.isArray(json.profiles) ? json.profiles : [];
    return {
      ok: true,
      profiles: rows.map((row) => {
        const r = row as Record<string, unknown>;
        return {
          id: String(r.id ?? ""),
          label: String(r.label ?? ""),
          recipient_name: String(r.recipient_name ?? ""),
          recipient_phone: String(r.recipient_phone ?? ""),
          delivery_method: r.delivery_method == null ? null : String(r.delivery_method),
          city_ref: r.city_ref == null ? null : String(r.city_ref),
          city_name: r.city_name == null ? null : String(r.city_name),
          warehouse_ref: r.warehouse_ref == null ? null : String(r.warehouse_ref),
          warehouse_name: r.warehouse_name == null ? null : String(r.warehouse_name),
          address_line: r.address_line == null ? null : String(r.address_line),
          is_default: Boolean(r.is_default),
        };
      }),
    };
  } catch (err) {
    const fail = networkFail(err);
    return { ok: false, profiles: [], error: fail.error, message: fail.message };
  }
}
